import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import NameSchema, { NameJSON, NameParams } from "_shared/names/name-schema";
import { ZodError } from "zod";

export default class Name {
  public static create(args: { params: unknown; options?: { silenceSchemaErrors: boolean } }): Name {
    try {
      const parsed = NameSchema.validate(args.params, !args.options?.silenceSchemaErrors);
      return new Name(parsed);
    } catch (error) {
      if (error instanceof Error && error.cause instanceof ZodError) {
        throw new InvalidNameError(error.message, {
          firstNameErrorMessage: error.cause.issues.find((issue) => issue.path.includes("firstName"))?.message ?? "",
          lastNameErrorMessage: error.cause.issues.find((issue) => issue.path.includes("lastName"))?.message ?? "",
        });
      } else {
        throw error;
      }
    }
  }

  public static fromJSON(json: unknown): Name | null | undefined {
    if (isNullOrUndefined(json)) return json;
    const parsed = NameSchema.deserialize(json);
    return new Name(parsed);
  }

  public readonly firstName: string;
  public readonly lastName: string;

  public get fullName(): string {
    return `${this.firstName} ${this.lastName}`;
  }

  public get initials(): string {
    return (`${this.firstName?.charAt(0)}` + `${this.lastName?.charAt(0)}`).toUpperCase();
  }

  public constructor(values: NameParams) {
    this.firstName = values.firstName;
    this.lastName = values.lastName;
  }

  public toJSON(): NameJSON {
    return NameSchema.serialize(this);
  }

  public isEqualTo(other: Name | null | undefined): boolean {
    return this.firstName === other?.firstName && this.lastName === other?.lastName;
  }

  public toString(): string {
    return `Name (
      firstName: ${this.firstName} 
      lastName: ${this.lastName}
    )`;
  }
}

export class InvalidNameError extends Error {
  public readonly firstNameErrorMessage: string;
  public readonly lastNameErrorMessage: string;
  constructor(message: string, context: { firstNameErrorMessage: string; lastNameErrorMessage: string }) {
    super(message);
    this.firstNameErrorMessage = context.firstNameErrorMessage;
    this.lastNameErrorMessage = context.lastNameErrorMessage;
  }
}
